import { motion } from 'framer-motion';
import { useRef } from 'react';
import { useInView } from 'framer-motion';
import { MessageCircle, Sparkles, ShieldCheck } from 'lucide-react';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

export const ValuePropsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const valueProps = [
    {
      icon: MessageCircle,
      title: "Focus on users, YakStak handles the notes",
      description: "Stay present in every conversation while we organize everything automatically."
    },
    {
      icon: Sparkles,
      title: "Themes in minutes, not days",
      description: "Skip the spreadsheet marathon — patterns emerge instantly across all interviews."
    },
    {
      icon: ShieldCheck,
      title: "Clear next steps from messy feedback",
      description: "Transform chaos into prioritized product decisions your team can act on immediately."
    }
  ];

  return (
    <section id="value-props" className="px-4 sm:px-6 py-12 sm:py-16 bg-gradient-subtle" aria-labelledby="value-props-heading" ref={ref}>
      <div className="max-w-4xl mx-auto text-center">
        <motion.div 
          className="mb-12"
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <motion.h2 
            id="value-props-heading" 
            className="text-3xl sm:text-4xl font-bold text-foreground mb-8"
            variants={fadeInUp}
          >
            Three things that make founders smile
          </motion.h2>
          
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto"
            variants={staggerContainer}
          >
            {valueProps.map((prop, index) => (
              <motion.div
                key={index}
                className="bg-card/80 rounded-xl p-6 border border-border"
                variants={fadeInUp}
                whileHover={{ 
                  scale: 1.05,
                  transition: { type: "spring", stiffness: 300, damping: 15 }
                }}
              >
                <div className="text-center">
                  <motion.div 
                    className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4 mx-auto"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                  >
                    <prop.icon className="w-6 h-6 text-primary" />
                  </motion.div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">{prop.title}</h3>
                  <p className="text-muted-foreground text-sm">
                    {prop.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};